/**
 * @fileoverview SessionViewerEventEmitter
 * 
 * A lightweight event emitter for SessionViewer that allows multiple components
 * to listen to session events without interfering with the existing callback prop system.
 * 
 * This provides a pub/sub pattern alongside the traditional callback props, giving
 * developers flexibility in how they handle events.
 * 
 * @module SessionViewer/events/SessionViewerEventEmitter
 */

/**
 * SessionViewerEventEmitter Class
 * 
 * A simple event emitter implementation that allows multiple listeners per event.
 * Events are namespaced by sessionSid to prevent cross-session event leakage.
 * 
 * @class
 * @example
 * // Listen to events
 * sessionViewerEvents.on('sessionLoaded', (data) => {
 *   if (data.sessionSid === mySessionSid) {
 *     console.log('My session loaded!');
 *   }
 * });
 * 
 * @example
 * // Listen to events for a specific session
 * sessionViewerEvents.onSession(sessionSid, 'sessionLoaded', (data) => {
 *   console.log('Session loaded:', data);
 * });
 * 
 * @example
 * // One-time listener
 * sessionViewerEvents.once('connected', (data) => {
 *   console.log('Connected once:', data);
 * });
 */
class SessionViewerEventEmitter {
  constructor() {
    /**
     * Map of event names to arrays of listener functions
     * @private
     */
    this.events = {};
    
    /**
     * Map of one-time listeners
     * @private
     */
    this.onceListeners = new WeakMap();
  }

  /**
   * Register an event listener
   * 
   * @param {string} eventName - The name of the event to listen to
   * @param {Function} listener - The callback function to execute when the event is triggered
   * @returns {Function} A function to remove this listener
   * 
   * @example
   * const removeListener = sessionViewerEvents.on('sessionLoaded', (data) => {
   *   console.log('Session loaded:', data);
   * });
   * 
   * // Later, remove the listener
   * removeListener();
   */
  on(eventName, listener) {
    if (typeof listener !== 'function') {
      throw new Error('Listener must be a function');
    }

    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }

    this.events[eventName].push(listener);

    // Return a function to remove this listener
    return () => this.off(eventName, listener);
  }

  /**
   * Register a one-time event listener
   * 
   * The listener will be automatically removed after it's called once.
   * 
   * @param {string} eventName - The name of the event to listen to
   * @param {Function} listener - The callback function to execute when the event is triggered
   * @returns {Function} A function to remove this listener
   * 
   * @example
   * sessionViewerEvents.once('sessionLoaded', (data) => {
   *   console.log('This will only log once');
   * });
   */
  once(eventName, listener) {
    if (typeof listener !== 'function') {
      throw new Error('Listener must be a function');
    }

    const onceWrapper = (...args) => {
      listener(...args);
      this.off(eventName, onceWrapper);
    };

    // Store reference to original listener for removal
    this.onceListeners.set(onceWrapper, listener);

    return this.on(eventName, onceWrapper);
  }

  /**
   * Register an event listener for a specific session
   * 
   * This is a convenience method that automatically filters events by sessionSid.
   * 
   * @param {string} sessionSid - The session ID to filter events for
   * @param {string} eventName - The name of the event to listen to
   * @param {Function} listener - The callback function to execute when the event is triggered
   * @returns {Function} A function to remove this listener
   * 
   * @example
   * sessionViewerEvents.onSession('session_123', 'sessionLoaded', (data) => {
   *   console.log('My specific session loaded:', data);
   * });
   */
  onSession(sessionSid, eventName, listener) {
    if (typeof listener !== 'function') {
      throw new Error('Listener must be a function');
    }

    const wrappedListener = (data) => {
      if (data.sessionSid === sessionSid) {
        listener(data);
      }
    };

    return this.on(eventName, wrappedListener);
  }

  /**
   * Remove an event listener
   * 
   * @param {string} eventName - The name of the event
   * @param {Function} listener - The listener function to remove
   * 
   * @example
   * const myListener = (data) => console.log(data);
   * sessionViewerEvents.on('sessionLoaded', myListener);
   * 
   * // Later...
   * sessionViewerEvents.off('sessionLoaded', myListener);
   */
  off(eventName, listener) {
    if (!this.events[eventName]) {
      return;
    }

    // Check if this is a once listener wrapper
    const originalListener = this.onceListeners.get(listener);
    const listenerToRemove = originalListener || listener;

    this.events[eventName] = this.events[eventName].filter(
      (l) => l !== listener && l !== listenerToRemove
    );

    if (this.events[eventName].length === 0) {
      delete this.events[eventName];
    }
  }

  /**
   * Remove all listeners for an event, or all events if no event name is provided
   * 
   * @param {string} [eventName] - Optional event name. If not provided, removes all listeners for all events.
   * 
   * @example
   * // Remove all listeners for a specific event
   * sessionViewerEvents.removeAllListeners('sessionLoaded');
   * 
   * @example
   * // Remove all listeners for all events
   * sessionViewerEvents.removeAllListeners();
   */
  removeAllListeners(eventName) {
    if (eventName) {
      delete this.events[eventName];
    } else {
      this.events = {};
    }
  }

  /**
   * Emit an event to all registered listeners
   * 
   * This is called internally by the SessionViewer when events occur.
   * 
   * @param {string} eventName - The name of the event to emit
   * @param {Object} data - The data to pass to the listeners
   * 
   * @example
   * sessionViewerEvents.emit('sessionLoaded', {
   *   sessionSid: 'session_123',
   *   roomId: 'room_456'
   * });
   */
  emit(eventName, data = {}) {
    if (!this.events[eventName]) {
      return;
    }

    // Call all listeners with the data
    // Use slice to avoid issues if a listener removes itself
    this.events[eventName].slice().forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error(`Error in event listener for ${eventName}:`, error);
      }
    });
  }

  /**
   * Get the number of listeners for an event
   * 
   * @param {string} eventName - The name of the event
   * @returns {number} The number of listeners
   * 
   * @example
   * const count = sessionViewerEvents.listenerCount('sessionLoaded');
   * console.log(`There are ${count} listeners for sessionLoaded`);
   */
  listenerCount(eventName) {
    return this.events[eventName]?.length || 0;
  }

  /**
   * Get all event names that have listeners
   * 
   * @returns {string[]} Array of event names
   * 
   * @example
   * const events = sessionViewerEvents.eventNames();
   * console.log('Events with listeners:', events);
   */
  eventNames() {
    return Object.keys(this.events);
  }
}

/**
 * Singleton instance of SessionViewerEventEmitter
 * 
 * This is the main instance that should be used throughout the application.
 * It's a singleton to ensure all components are listening to the same event stream.
 * 
 * @type {SessionViewerEventEmitter}
 */
export const sessionViewerEvents = new SessionViewerEventEmitter();

/**
 * Export the class for testing or creating custom instances
 */
export default SessionViewerEventEmitter;
